import type { JSX } from 'react';
import { VisibilityOutlined, FavoriteBorder } from '@mui/icons-material';
import DSBox from '../../design-system/DSBox';
import DSAvatar from '../../design-system/DSAvatar';
import DSTypography from '../../design-system/Typography/DSTypography';
import { KlydoPreview } from '../../api/public';
import {
  KLYDO_BLACK,
  CLOCK_INFO_LABEL,
  CLOCK_INFO_MUTED,
} from '../../consts/colors';
import { clockFacePx, clockFaceSize } from './clockFaceScale';

type KlydoInfoProps = {
  klydoPreview: KlydoPreview;
  scale?: number;
};

const formatDate = (date?: Date): string => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

/**
 * "Klydo Info" tab — information about the klydo itself.
 * Ported 1:1 from the v3 Kotlin/Compose KlydoInfoPage (1080px face reference):
 * artist photo → "By {artist}" → klydo name → subtitle → views + favorites → created date.
 */
const KlydoInfo = ({
  klydoPreview,
  scale = 1,
}: KlydoInfoProps): JSX.Element => {
  const {
    author: artist,
    name: klydoName,
    subtitle,
    createdAt,
    stats,
  } = klydoPreview;

  const statsIconSize = clockFaceSize(70, scale);
  const created = formatDate(createdAt);

  return (
    <DSBox
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        overflow: 'hidden',
        background: KLYDO_BLACK,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingX: clockFacePx(40, scale),
        paddingTop: clockFacePx(30, scale),
        textAlign: 'center',
      }}
    >
      <DSAvatar
        src={artist?.photoUrl}
        alt={artist?.name}
        sx={{
          width: clockFacePx(150, scale),
          height: clockFacePx(150, scale),
          borderRadius: '50%',
        }}
      />

      <DSTypography
        sx={{
          marginTop: clockFacePx(20, scale),
          fontFamily: 'Gilroy-Bold',
          color: CLOCK_INFO_LABEL,
          fontSize: clockFacePx(62, scale),
          lineHeight: 1.1,
        }}
      >
        By {artist?.name}
      </DSTypography>

      <DSTypography
        sx={{
          marginTop: clockFacePx(50, scale),
          fontFamily: 'Gilroy-Bold',
          color: '#FFFFFF',
          fontSize: clockFacePx(70, scale),
          lineHeight: 1.1,
          width: '83%',
        }}
      >
        {klydoName}
      </DSTypography>

      {subtitle?.trim() && (
        <DSTypography
          sx={{
            marginTop: clockFacePx(30, scale),
            fontFamily: 'Gilroy-Light',
            color: '#FFFFFF',
            fontSize: clockFacePx(38, scale),
            lineHeight: 1.2,
            width: '83%',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {subtitle}
        </DSTypography>
      )}

      <DSBox
        sx={{
          marginTop: clockFacePx(50, scale),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: clockFacePx(30, scale),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: clockFacePx(10, scale),
          }}
        >
          <VisibilityOutlined
            sx={{ fontSize: statsIconSize, color: CLOCK_INFO_MUTED }}
          />
          <DSTypography
            sx={{
              fontFamily: 'Gilroy-Light',
              fontSize: clockFacePx(70, scale),
              color: CLOCK_INFO_MUTED,
            }}
          >
            {stats?.viewed ?? 0}
          </DSTypography>
        </DSBox>

        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: clockFacePx(10, scale),
          }}
        >
          <FavoriteBorder
            sx={{ fontSize: statsIconSize, color: CLOCK_INFO_MUTED }}
          />
          <DSTypography
            sx={{
              fontFamily: 'Gilroy-Light',
              fontSize: clockFacePx(70, scale),
              color: CLOCK_INFO_MUTED,
            }}
          >
            {Math.max(stats?.favorites ?? 0, 0)}
          </DSTypography>
        </DSBox>
      </DSBox>

      {created && (
        <DSTypography
          sx={{
            marginTop: clockFacePx(50, scale),
            fontFamily: 'Gilroy-Light',
            fontSize: clockFacePx(60, scale),
            color: CLOCK_INFO_MUTED,
          }}
        >
          Created: {created}
        </DSTypography>
      )}
    </DSBox>
  );
};

export default KlydoInfo;
